import { useEffect, useMemo, useState } from "react";
import {
  Background, ControlButton, Controls, Handle, MiniMap, Position, ReactFlow, useNodesState,
  type Connection, type Edge, type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import Dagre from "@dagrejs/dagre";
import { useGraphFullscreen } from "./useGraphFullscreen";
import { ParcelTrackModal } from "./ParcelTrackModal";
import { RouteImpactModal } from "./RouteImpactModal";
import { Badge } from "@/components/ui/Badge";
import { SectionCard, milestoneMeta, usePersistedFlag } from "../components/ui";
import { MilestoneCounts } from "../components/MilestoneCounts";
import { FactProposalsBanner } from "../components/FactProposalsBanner";
import { MilestoneDetailModal } from "../components/MilestoneDetailModal";
import { fmtDate } from "../utils";
import type { MilestoneState, PowerProject } from "../types";
import { useClearGraphPositions, useProcessGraph, useGraphEdge, useGraphNodePosition, useProcessTemplates, useResetProcessGraph, useRouteImpact } from "./hooks";
import type { GraphNode, ProcessGraph } from "./types";

// Kolory i etykiety stanów — ta sama mapa co na tablicy kamieni (components/ui),
// żeby ten sam krok czytał się identycznie na kanbanie i na schemacie blokowym.
const stateStyle = (state: string) => milestoneMeta(state as MilestoneState);

const FLAG: Record<string, { color: Parameters<typeof Badge>[0]["color"]; label: string }> = {
  on_track: { color: "green", label: "Zdążysz" },
  at_risk: { color: "yellow", label: "Ryzyko" },
  will_miss: { color: "red", label: "Nie zdążysz" },
  unknown: { color: "gray", label: "—" },
};

// Wewnętrzne fazy kroku: ● zrobione / ▶ trwa / ○ czeka; faza 2 = strona zewnętrzna.
const PHASE_MARK: Record<string, string> = { done: "●", active: "▶", pending: "○" };

const NODE_W = 210;

// Wysokość węzła szacowana z liczby wierszy — teksty NIE są ucinane (multiline),
// więc dagre musi dostać realny rozmiar prostokąta.
const _lines = (text: string, perLine: number) => Math.max(1, Math.ceil((text ?? "").length / perLine));

function phaseText(p: NonNullable<GraphNode["phases"]>[number]): string {
  const date = p.done_at ? ` · ${fmtDate(p.done_at)}` : p.since ? ` · od ${fmtDate(p.since)}` : "";
  return `${p.label}${p.sla_days ? ` (${p.sla_days}d)` : ""}${date}`;
}

function nodeHeight(n: GraphNode): number {
  let h = 26; // padding + linia stanu
  h += _lines(n.label, 22) * 15; // nagłówek (bold 12px)
  if (n.sublabel) h += 13;
  if (n.is_wpw && n.clock_side) h += 15; // linia zegara lokalizacji
  if (n.is_wpw && n.phase_label) h += _lines(n.phase_label, 32) * 13;
  if (n.is_wpw && n.track_failed) h += _lines(`✗ ${n.track_failed} — nieudany`, 32) * 13;
  if (n.is_wpw && (n.track_queue?.length ?? 0) > 0) {
    h += _lines(`w kolejce: ${n.track_queue!.join(" → ")}`, 32) * 13;
  }
  // „Nie dotyczy" — krok pominięty, więc fazy i wyliczone daty nie mają znaczenia
  // i nie są renderowane; nie doliczaj ich do wysokości.
  if (n.state !== "na") {
    (n.phases ?? []).forEach((p) => { h += _lines(phaseText(p), 34) * 13 + 2; });
    if (n.order_ref) {
      h += 13; // jedna linia „termin zamówienia" zamiast pary start/→
    } else {
      if (n.es) h += 13;
      if (n.ef) h += 13;
    }
  }
  if (!n.is_wpw && !n.expandable) h += 14; // linia „notatki · pliki"
  if (n.violation && !n.is_wpw) h += _lines(`wymaga: ${n.missing_prereqs.join(", ")}`, 30) * 13 + 2;
  if (n.revert_note) h += _lines(`cofnięto: ${n.revert_note}`, 30) * 13 + 2;
  return h + 12;
}

function StageNode({ data }: { data: GraphNode }) {
  const st = stateStyle(data.state);
  const isWpw = !!data.is_wpw;
  const isArt124 = !!data.is_art124;
  const isLoc = !!data.expandable;
  return (
    <div
      className="rounded-lg border shadow-sm text-xs"
      style={{
        width: NODE_W, padding: "8px 12px", background: st.bg, color: st.fg,
        borderColor: isArt124 ? "#dc2626"
          : data.violation ? "#dc2626" : isLoc || isWpw ? "#7c3aed" : data.is_critical ? "#ea580c" : data.state === "na" ? "#374151" : "rgba(0,0,0,0.12)",
        borderWidth: data.violation || data.is_critical || isLoc || isArt124 ? 2 : 1,
        borderStyle: isWpw || data.is_manual ? "dashed" : "solid",
        cursor: isWpw ? "pointer" : undefined,
      }}
      title={isWpw && !isArt124 ? "Kliknij — tor uzgodnienia działki (bloczki + zegar)" : undefined}
    >
      <Handle
        type="target"
        position={Position.Left}
        title={data.es ? `Wejście — najwcześniejszy start: ${fmtDate(data.es)}` : "Wejście (od czego zależy)"}
        style={{ background: "#64748b", width: 12, height: 12, border: "2px solid #fff", cursor: "pointer" }}
      />
      <div className="flex items-start justify-between gap-1">
        <span className="font-bold whitespace-normal break-words leading-tight">
          {isArt124 ? `⚖ ${data.label}` : isWpw ? `📍 ${data.label}` : data.label}
        </span>
        <span className="shrink-0">
          {isArt124 && <span title="Eskalacja art. 124 UGN" style={{ color: "#dc2626", fontWeight: 700 }}>ART.124</span>}
          {data.is_manual && <span title="Krok dodany ręcznie" style={{ opacity: 0.7 }}>✎</span>}
          {data.is_critical && <span title="Ścieżka krytyczna" style={{ color: "#ea580c", fontWeight: 700 }}>◆</span>}
          {isLoc && <span title="Rozwijalny na konkretne WPW (przełącznik nad grafem)" style={{ color: "#7c3aed" }}>⤢</span>}
        </span>
      </div>
      {data.sublabel && <div className="text-[10px] opacity-70 whitespace-normal break-words">{data.sublabel}</div>}
      {isWpw && data.clock_side && (
        // zegar lokalizacji: strona · dni/limit — czerwone po terminie, bursztyn blisko
        <div
          className="font-mono text-[10px] font-bold mt-0.5"
          style={{ color: data.clock_breached ? "#dc2626" : data.clock_warn ? "#b45309" : "#334155" }}
        >
          {data.clock_side === "ours" ? "MY" : (data.counterparty_label ?? "ONI").toUpperCase()}{" "}
          {data.clock_days_in}/{data.clock_limit_days ?? "—"} dni
          {data.clock_breached ? " ⚠" : ""}
        </div>
      )}
      {isWpw && data.phase_label && (
        <div className="text-[10px] opacity-80 whitespace-normal break-words leading-tight">{data.phase_label}</div>
      )}
      {isWpw && data.track_failed && (
        // skrót toru: ostatni nieudany bloczek (np. odmowa negocjatora) bez otwierania modala
        <div className="text-[10px] font-semibold whitespace-normal break-words leading-tight" style={{ color: "#dc2626" }}>
          ✗ {data.track_failed} — nieudany
        </div>
      )}
      {isWpw && (data.track_queue?.length ?? 0) > 0 && (
        <div className="text-[10px] opacity-60 whitespace-normal break-words leading-tight">
          w kolejce: {data.track_queue!.join(" → ")}
        </div>
      )}
      <div className="mt-0.5 opacity-80">
        {st.label}
        {data.state !== "not_started" && data.state_since ? ` od ${fmtDate(data.state_since)}` : ""}
        {data.sla_days ? ` · SLA ${data.sla_days}d` : ""}
      </div>
      {data.state !== "na" && (data.phases ?? []).length > 0 && (
        <div className="mt-0.5 space-y-px">
          {data.phases!.map((p) => (
            <div
              key={p.name}
              className="text-[10px] whitespace-normal break-words leading-tight"
              style={{
                opacity: p.status === "pending" ? 0.55 : 1,
                fontWeight: p.status === "active" ? 700 : 400,
                color: p.status === "done" ? "#16a34a" : undefined,
              }}
            >
              {PHASE_MARK[p.status]} {phaseText(p)}
            </div>
          ))}
        </div>
      )}
      {/* „Nie dotyczy" — krok pominięty, wyliczone daty/termin nie mają znaczenia. */}
      {data.state !== "na" &&
        (data.order_ref ? (
          // Krok jest już zlecony realnym zamówieniem — pokazujemy jego termin,
          // nie daty wyliczone z łańcucha SLA.
          <div className="font-mono text-[10px] font-semibold" title={`Zamówienie ${data.order_ref.number}`}>
            termin zamówienia: {fmtDate(data.order_ref.deadline)}
          </div>
        ) : (
          <>
            {data.es && <div className="font-mono text-[10px] opacity-60">start {fmtDate(data.es)}</div>}
            {data.ef && <div className="font-mono text-[10px] opacity-70">→ {fmtDate(data.ef)}</div>}
          </>
        ))}
      {/* notatki i pliki kroku — te same liczby co na karcie na tablicy kamieni;
          klik w kafelek otwiera ten sam modal ze szczegółami */}
      {!isWpw && !isLoc && (
        <MilestoneCounts notes={data.note_count} files={data.file_count} className="mt-0.5 opacity-80" />
      )}
      {data.violation && !isWpw && (
        <div className="text-[10px] font-semibold text-red-700 mt-0.5 whitespace-normal break-words">
          ⚠ wymaga: {data.missing_prereqs.join(", ")}
        </div>
      )}
      {data.revert_note && (
        <div className="text-[10px] mt-0.5 whitespace-normal break-words" style={{ color: "#b45309" }}>
          ↩ cofnięto: {data.revert_note}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        title={data.ef ? `Wyjście — najwcześniejszy koniec: ${fmtDate(data.ef)}` : "Wyjście (co od tego zależy)"}
        style={{ background: "#0ea5e9", width: 12, height: 12, border: "2px solid #fff", cursor: "pointer" }}
      />
    </div>
  );
}

const nodeTypes = { stage: StageNode };

/** Kroki „Nie dotyczy” (czarne kafelki) — pominięte w tym projekcie, nikt ich nie
 *  rusza, a zajmują pół planszy. Domyślnie ich nie pokazujemy. Filtrujemy PRZED
 *  dagre, żeby układ się ścieśnił, i razem z węzłem znikają jego krawędzie.
 *  To wyłącznie widok: daty, ścieżka krytyczna i termin liczą się na backendzie
 *  z pełnego grafu i nie zmieniają się od tego przełącznika. */
export function withoutNa(graph: ProcessGraph): ProcessGraph {
  const hidden = new Set(graph.nodes.filter((n) => n.state === "na").map((n) => n.key));
  if (!hidden.size) return graph;
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => !hidden.has(n.key)),
    edges: graph.edges.filter((e) => !hidden.has(e.source) && !hidden.has(e.target)),
  };
}

function layout(graph: ProcessGraph): { nodes: Node[]; edges: Edge[] } {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 26, ranksep: 80, marginx: 12, marginy: 12 });
  graph.nodes.forEach((n) => g.setNode(n.key, { width: NODE_W, height: nodeHeight(n) }));
  graph.edges.forEach((e) => g.setEdge(e.source, e.target));
  Dagre.layout(g);
  const nodes: Node[] = graph.nodes.map((n) => {
    const p = g.node(n.key);
    // Ręcznie przesunięty kafelek honoruje zapisane x/y; reszta = auto-układ dagre.
    const manual = n.x != null && n.y != null;
    return {
      id: n.key, type: "stage",
      position: manual
        ? { x: n.x as number, y: n.y as number }
        : { x: p.x - NODE_W / 2, y: p.y - nodeHeight(n) / 2 },
      data: n as unknown as Record<string, unknown>,
    };
  });
  const edges: Edge[] = graph.edges.map((e) => ({
    id: `${e.source}->${e.target}`,
    source: e.source,
    target: e.target,
    animated: e.on_critical,
    style: {
      stroke: e.on_critical ? "#ea580c" : e.satisfied ? "#16a34a" : "#94a3b8",
      strokeWidth: e.on_critical ? 2.5 : 1.5,
      strokeDasharray: e.is_default ? undefined : "5 4",
    },
    markerEnd: { type: "arrowclosed" as const, color: e.on_critical ? "#ea580c" : "#94a3b8" },
    data: { node: e.target, depends_on: e.source, is_default: e.is_default },
  }));
  return { nodes, edges };
}

export function ProcessGraphView({ connection }: { connection: PowerProject }) {
  const connectionId = connection.id;
  const [expand, setExpand] = useState(false);
  // Domyślnie ON — graf ma pokazywać to, co się realnie dzieje.
  const [hideNa, setHideNa] = usePersistedFlag("elg5:ze-graph-hide-na", true);
  const { data, isLoading } = useProcessGraph(connectionId, expand);
  const edgeM = useGraphEdge(connectionId);
  const posM = useGraphNodePosition(connectionId);
  const clearPosM = useClearGraphPositions(connectionId);
  const resetM = useResetProcessGraph(connectionId);
  const { data: templates } = useProcessTemplates();
  const [resetTpl, setResetTpl] = useState("");
  // klik kafelka → TEN SAM modal szczegółów co z tablicy kamieni (status, terminy,
  // fazy, historia, notatki, pliki). Trzymamy klucz kroku, nie snapshot węzła, żeby
  // modal czytał żywy kamień z projektu.
  const [selKey, setSelKey] = useState<string | null>(null);
  // klik pod-węzła działki → panel toru (bloczki + zegar)
  const [trackParcel, setTrackParcel] = useState<{ id: string; ident: string } | null>(null);
  // rykoszet przeprojektowania: działki z flagą route_review do decyzji projektanta
  const { data: impact } = useRouteImpact(connectionId);
  const [impactOpen, setImpactOpen] = useState(false);
  const { isFull, toggle: toggleFull } = useGraphFullscreen();

  // Układ z danych (dagre + zapisane pozycje). Węzły trzymamy w stanie React Flow,
  // żeby dało się je przeciągać; przy każdym świeżym payloadzie resync do układu.
  const laid = useMemo(
    () => (data ? layout(hideNa ? withoutNa(data) : data) : { nodes: [], edges: [] }),
    [data, hideNa],
  );
  const naCount = data?.nodes.filter((n) => n.state === "na").length ?? 0;
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  useEffect(() => setNodes(laid.nodes), [laid, setNodes]);
  const edges = laid.edges;
  const flag = FLAG[data?.deadline_flag ?? "unknown"] ?? FLAG.unknown;
  // Kamień stojący za klikniętym kafelkiem (węzły syntetyczne nie mają swojego).
  const selMilestone = selKey ? connection.milestones.find((m) => m.key === selKey) ?? null : null;
  const tplOptions = templates ?? [];
  const currentTpl = data?.template ?? null;

  const doReset = () => {
    const chosen = tplOptions.find((t) => t.id === resetTpl);
    const name = chosen?.name ?? "szablonu klienta (lub domyślnego)";
    if (!window.confirm(
      `Zresetować proces tego projektu do szablonu „${name}"?\n\n` +
      "Krawędzie i SLA wrócą do szablonu, ręcznie dodane zależności znikną. " +
      "Stany kroków o tych samych kluczach oraz kroki dodane ręcznie ZOSTANĄ.",
    )) return;
    resetM.mutate({ templateId: resetTpl || undefined });
  };

  // Kosmetyka: kasuje zapisane pozycje kafelków → cała plansza wraca do auto-układu
  // (dagre). Merytorycznie NIC się nie zmienia — kroki, stany, SLA i zależności zostają.
  const doRelayout = () => {
    if (!window.confirm(
      "Rozmieścić kafelki od nowa (automatycznie)?\n\n" +
      "Ręcznie ustawione pozycje kafelków zostaną skasowane. " +
      "Kroki, stany, SLA i zależności NIE zmienią się.",
    )) return;
    clearPosM.mutate();
  };

  return (
    <SectionCard
      title="Graf procesu (DAG) — symulacja przepływu"
      action={
        data && (
          <div className="flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={() => setExpand((v) => !v)}
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono uppercase tracking-wide ${expand ? "border-[#7c3aed] text-[#7c3aed]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}
              title="Pokaż każdą działkę jako osobny prostokąt WPW feedujący uzgodnienie lokalizacji"
            >
              {expand ? "▾ WPW: osobne działki" : "▸ Rozbij lokalizację na WPW"}
            </button>
            <button
              type="button"
              onClick={() => setHideNa(!hideNa)}
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono uppercase tracking-wide ${hideNa ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}
              title={
                hideNa
                  ? "Kroki „Nie dotyczy” są ukryte — kliknij, żeby pokazać cały graf. Terminy i ścieżka krytyczna liczą się z pełnego grafu tak czy inaczej."
                  : "Ukryj kroki „Nie dotyczy” — pominięte w tym projekcie, nikt ich nie robi."
              }
            >
              {hideNa ? "☑" : "☐"} Ukryj „Nie dotyczy”
              {naCount > 0 ? ` (${naCount})` : ""}
            </button>
            <span className="text-[var(--color-text-muted)]">Zakończenie:</span>
            <span className="font-mono">{data.completion_date ? fmtDate(data.completion_date) : "—"}</span>
            <Badge color={flag.color}>{flag.label}</Badge>
          </div>
        )
      }
    >
      {isLoading ? (
        <div className="text-sm text-[var(--color-text-muted)]">Ładowanie grafu…</div>
      ) : (
        <>
          <FactProposalsBanner connectionId={connectionId} />
          {(impact?.parcels.length ?? 0) > 0 && (
            <div className="flex items-center gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 mb-2 rounded">
              <span className="flex-1">
                ⚠ Zmiana trasy dotknęła <b>{impact!.parcels.length}</b> działek z postępem
                pozyskania — zdecyduj, które cofają się do stanu startowego.
              </span>
              <button className="underline font-bold" onClick={() => setImpactOpen(true)}>Zdecyduj →</button>
            </div>
          )}
          <div className={isFull
            ? "fixed inset-0 z-40 bg-[var(--color-bg-elevated)]"
            : "h-[560px] rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"}>
            <ReactFlow
              key={isFull ? "fs" : "inline"}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              colorMode="system"
              proOptions={{ hideAttribution: true }}
              nodesConnectable
              connectOnClick
              onNodesChange={onNodesChange}
              onNodeDragStop={(_e, node) => {
                // Przeciągnięto kafelek → zapamiętaj pozycję (kosmetyka, bez ruszania zależności).
                // wpw:/wpw124: to węzły syntetyczne rozwinięcia — też można je układać.
                posM.mutate({ node: node.id, x: Math.round(node.position.x), y: Math.round(node.position.y) });
              }}
              onConnect={(conn: Connection) => {
                // handle Right (source) = prerequisite, handle Left (target) = dependent.
                // Edge source→target means "target depends on source" (target musi czekać na source).
                if (!conn.source || !conn.target || conn.source === conn.target) return;
                // syntetyczne WPW (wpw:<id>) są tylko poglądowe — linkujemy węzeł ogólny.
                if (conn.source.startsWith("wpw:") || conn.target.startsWith("wpw:")) return;
                edgeM.mutate({ node: conn.target, depends_on: conn.source });
              }}
              onEdgeClick={(_e, edge) => {
                const d = edge.data as { node: string; depends_on: string } | undefined;
                if (d && window.confirm(`Usunąć zależność „${d.node} ← ${d.depends_on}"?`)) {
                  edgeM.mutate({ node: d.node, depends_on: d.depends_on, remove: true });
                }
              }}
              onNodeClick={(_e, n) => {
                const gn = n.data as unknown as GraphNode;
                if (gn.is_wpw) {
                  // pod-węzeł działki (wpw:<id> / wpw124:<id>) → panel toru uzgodnienia
                  const parcelId = gn.ref_parcel_id
                    ?? gn.key.replace(/^wpw124:/, "").replace(/^wpw:/, "");
                  setTrackParcel({ id: parcelId, ident: gn.label.replace(/^(Działka|Art\. 124 — dz\.)\s*/, "") });
                  return;
                }
                if (gn.expandable) return;
                setSelKey(gn.key);
              }}
            >
              <Background gap={16} />
              <Controls showInteractive={false}>
                <ControlButton
                  onClick={() => {
                    if (window.confirm("Rozmieścić kafelki automatycznie (dagre)?\n\nRęcznie ustawione pozycje zostaną usunięte. Zależności i dane pozostają bez zmian."))
                      clearPosM.mutate();
                  }}
                  title="Auto-rozmieszczenie — usuń ręczne pozycje kafelków"
                >
                  ▦
                </ControlButton>
                <ControlButton onClick={toggleFull} title={isFull ? "Zamknij pełny ekran (Esc)" : "Pełny ekran"}>
                  {isFull ? "✕" : "⛶"}
                </ControlButton>
              </Controls>
              <MiniMap pannable zoomable />
            </ReactFlow>
          </div>

          {/* modal szczegółów kroku — wspólny z tablicą kamieni */}
          {selMilestone && (
            <MilestoneDetailModal
              connection={connection}
              milestone={selMilestone}
              onClose={() => setSelKey(null)}
            />
          )}
          {trackParcel && (
            <ParcelTrackModal parcelId={trackParcel.id} ident={trackParcel.ident}
              onClose={() => setTrackParcel(null)} />
          )}
          {impactOpen && (
            <RouteImpactModal connectionId={connectionId} onClose={() => setImpactOpen(false)} />
          )}

          {/* szablon procesu: skąd wzięła się struktura + reset do szablonu */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[var(--color-border)] text-xs">
            <span className="text-[var(--color-text-muted)]">
              Szablon procesu:{" "}
              <b>{currentTpl ? currentTpl.name : "— (projekt sprzed szablonów)"}</b>
            </span>
            <span className="flex-1" />
            <select className="input-base text-xs" value={resetTpl} onChange={(e) => setResetTpl(e.target.value)}>
              <option value="">szablon klienta (auto)</option>
              {tplOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (domyślny)" : t.client_name ? ` — ${t.client_name}` : ""}</option>
              ))}
            </select>
            <button
              type="button"
              className="inline-flex items-center gap-1 font-mono uppercase tracking-wider px-3 py-1.5 border border-[var(--color-border)] text-[var(--color-text-secondary)] disabled:opacity-50"
              disabled={clearPosM.isPending}
              onClick={doRelayout}
              title="Czysta kosmetyka: kasuje ręcznie zapisane pozycje kafelków i układa je automatycznie (dagre). Kroki, stany, SLA i zależności bez zmian."
            >
              ▦ {clearPosM.isPending ? "Układam…" : "Resetuj rozmieszczenie kafli"}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 font-mono uppercase tracking-wider px-3 py-1.5 border border-[var(--color-primary)] text-[var(--color-primary)] disabled:opacity-50"
              disabled={resetM.isPending}
              onClick={doReset}
              title="Przywraca kroki, fazy, SLA i zależności z szablonu. Stany kroków i kroki ręczne zostają."
            >
              ⟲ Resetuj do szablonu
            </button>
            {resetM.isError && <span className="text-[var(--color-primary)]">Nie udało się zresetować.</span>}
            {clearPosM.isError && <span className="text-[var(--color-primary)]">Nie udało się rozmieścić kafelków.</span>}
            {edgeM.isError && <span className="text-[var(--color-primary)]">Nie dodano zależności (cykl?).</span>}
          </div>

          <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2 flex flex-wrap gap-3">
            <span>— ciągła: z szablonu</span><span>- - - przerywana: ręczna</span>
            <span style={{ color: "#ea580c" }}>◆ ścieżka krytyczna</span>
            <span style={{ color: "#dc2626" }}>⚠ naruszenie kolejności</span>
            <span>● / ▶ / ○ fazy kroku (zrobione / trwa / czeka)</span>
            <span>przeciągnij kropki węzłów = dodaj zależność</span>
            <span>przeciągnij kafelek = przestaw (pozycja zapisana)</span>
            <span>klik krawędź = usuń</span>
            <span>klik kamień = te same szczegóły co na tablicy (status, notatki, pliki)</span>
            <span>nowy krok: zakładka „Kamienie” → pojawi się na grafie</span>
          </div>
        </>
      )}
    </SectionCard>
  );
}
